/**
 * /transit/sign-in — transit's own sign-in (not the Django one): email and
 * password, then back to where the user was going (?next=).
 */
import { useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Checkbox, Input, PasswordInput } from '../../components/ui'
import { SYSTEM_NOTICE } from '../Login'
import { transitAuth } from '../../services/transit'
import { useBridgeTheme } from '../../components/transit/theme'

/** Only a path within transit, so ?next= can't send anyone off-site. */
export function nextPath(next: string | null): string {
  return next && next.startsWith('/transit') && !next.startsWith('//') ? next : '/transit'
}

/** The Bridge sign-in layout: the form on the left, the photo on the right. */
export function AuthCard({ title, children }: { title: string; children: React.ReactNode }) {
  useBridgeTheme()
  return (
    // Exactly the screen's height: the photo fills it and never makes the page
    // scroll; a form taller than a short screen scrolls in its own column.
    <div className="flex h-screen w-full overflow-hidden bg-background">
      {/* Top-aligned, not centered: centering left a tall gap above the logo. */}
      <div className="flex flex-1 items-start justify-center overflow-y-auto px-10 pb-12 pt-10">
        <div className="flex w-full max-w-[454px] flex-col gap-10">
          {/* 24px under the logo (-mb-4 off the column's 40px gap). */}
          <img src="/o_logo_navy.png" alt="Overmatch" className="-mb-4 h-11 w-fit dark:hidden" />
          <img src="/o_logo_white.png" alt="" aria-hidden className="-mb-4 hidden h-11 w-fit dark:block" />
          <div className="flex flex-col gap-1 text-2xl font-medium leading-[1.2]">
            <p className="text-foreground/80">Welcome to Overmatch</p>
            <p className="text-muted-foreground">The acquisition planning workspace for DoW professionals</p>
          </div>
          <div className="flex flex-col gap-6">
            <h1 className="text-lg font-medium text-foreground">{title}</h1>
            {children}
          </div>
        </div>
      </div>
      <div className="hidden h-full w-[34%] max-w-[560px] shrink-0 p-4 lg:block">
        <img src="/transit/sign-in-bridge.jpg" alt="" aria-hidden className="h-full w-full object-cover" />
      </div>
    </div>
  )
}

export function SystemNotice({ acknowledged, onChange }: { acknowledged: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="max-h-40 overflow-y-auto rounded-md bg-secondary p-3 text-sm leading-normal text-foreground">{SYSTEM_NOTICE}</div>
      <Checkbox
        checked={acknowledged}
        onChange={(e) => onChange(e.target.checked)}
        label="I have read and acknowledge this system use notification."
      />
    </div>
  )
}

export function FormError({ error }: { error: string }) {
  if (!error) return null
  return (
    <div role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
      {error}
    </div>
  )
}

export default function TransitSignIn() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const [params] = useSearchParams()

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await transitAuth.signIn(email, password)
      navigate(nextPath(params.get('next')), { replace: true })
    } catch (err: any) {
      setError(err.message || 'Sign-in failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthCard title="Sign in">
      <form onSubmit={submit} className="flex flex-col gap-6">
        <div className="flex flex-col gap-6">
          <Input id="transit-email" label="Email" type="email" autoComplete="email" placeholder="Enter email" value={email}
            onChange={(e) => setEmail(e.target.value)} required disabled={loading} className="h-[53px] px-4 md:text-base" />
          <PasswordInput id="transit-password" label="Password" autoComplete="current-password" placeholder="Enter password" value={password}
            onChange={(e) => setPassword(e.target.value)} required disabled={loading} className="h-[53px] pl-4 md:text-base" />
        </div>
        <FormError error={error} />
        <SystemNotice acknowledged={acknowledged} onChange={setAcknowledged} />
        <Button type="submit" size="lg" className="h-12 w-full text-base" isLoading={loading} disabled={loading || !acknowledged}>
          Sign in
        </Button>
        <p className="text-center text-xs text-muted-foreground">Forgot your password? Ask your org's admin for a reset link.</p>
      </form>
    </AuthCard>
  )
}
